import * as React from 'react';
import { useState, useCallback, useReducer, useRef, useEffect } from 'react';
import { API } from 'aws-amplify';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import gqlTag from 'graphql-tag';
import * as queries from '../../graphql/queries';
import * as mutations from '../../graphql/mutations';
import {
  Deal,
  DealType,
  DealStage,
  DealTypeConfig,
  DealsByStage,
  PersonOption,
} from '../types/pipeline.types';

// Workaround: graphql-tag's DocumentNode is incompatible with Amplify's
// bundled graphql types. Casting the result to any resolves the version mismatch.
const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin: boolean;
    name: string;
  };
  appSettings: {
    labels: {
      person: { plural: string };
    };
    features: {
      deal: {
        disableUserFilter?: boolean;
        disableCircleFilter?: boolean;
      };
    };
  };
};

const UPDATE_STAGE = 'UPDATE_STAGE';
const SET_DEALS = 'SET_DEALS';

/**
 * The mission/deal lambdas report failures inside a successful GraphQL response
 * (e.g. the string "FAIL", or {"status": "<traceback>"}), so awaiting the call is
 * not enough to know it worked. Returns the reported status, lowercased.
 */
function parseMutationStatus(raw: unknown): string {
  if (raw == null) return 'no response';
  let value: any = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return value.toLowerCase();
    }
  }
  if (value && typeof value === 'object') {
    // Only `status` is a status. `body` is prose that accompanies a statusCode,
    // so reading it as one turns a 200 into a failure.
    if (value.statusCode !== undefined) {
      return value.statusCode === 200
        ? 'success'
        : `status ${value.statusCode}`;
    }
    return String(value.status ?? 'unknown').toLowerCase();
  }
  return String(value).toLowerCase();
}

type DealsAction =
  | { type: 'SET_DEALS'; payload: DealsByStage }
  | {
      type: 'UPDATE_STAGE';
      payload: { stageId: string; updates: Partial<DealsByStage[string]> };
    };

function dealsReducer(state: DealsByStage, action: DealsAction): DealsByStage {
  switch (action.type) {
    case SET_DEALS:
      return { ...state, ...action.payload };
    case UPDATE_STAGE:
      return {
        ...state,
        [action.payload.stageId]: {
          ...state[action.payload.stageId],
          ...action.payload.updates,
        },
      };
    default:
      return state;
  }
}

// --- Pure fetch functions ---

async function fetchDealsForStage(
  catId: string,
  stageId: string,
  ownerId: string,
  search: string,
  sortOrder: 'ASC' | 'DESC',
  displayFields: string[],
  offset = 0
): Promise<{ deals: Deal[]; canLoadMore: boolean }> {
  const res: any = await API.graphql({
    query: queries.listAllDeals,
    variables: {
      request: JSON.stringify({
        categoryID: catId,
        stageID: stageId,
        assigneeID: ownerId === 'all' ? '' : ownerId,
        searchString: search,
        sortOrder,
        limit: 25,
        offset,
        displayFields,
      }),
    },
  });

  const parsed = JSON.parse(res.data.listAllDeals);
  const deals: Deal[] = (parsed.deals || []).map((deal: any) => ({
    ...deal,
    updatedAt: new Date(deal.updatedAt),
  }));

  return { deals, canLoadMore: parsed.canLoadMore };
}

async function fetchAllStagesDeals(
  stages: DealStage[],
  catId: string,
  displayFields: string[],
  ownerId: string,
  search: string,
  sortOrders: Record<string, 'ASC' | 'DESC'>
): Promise<DealsByStage> {
  const results = await Promise.all(
    stages.map(async (stage) => {
      const order = sortOrders[stage.id] || 'DESC';
      try {
        const { deals, canLoadMore } = await fetchDealsForStage(
          catId,
          stage.id,
          ownerId,
          search,
          order,
          displayFields
        );
        return {
          stageId: stage.id,
          data: {
            deals,
            sortOrder: order,
            currentOffset: deals.length,
            canLoadMore,
            isLoading: false,
          },
        };
      } catch (err) {
        console.error('Error fetching deals for stage:', stage.id, err);
        return {
          stageId: stage.id,
          data: {
            deals: [],
            sortOrder: order,
            currentOffset: 0,
            canLoadMore: false,
            isLoading: false,
          },
        };
      }
    })
  );

  const byStage: DealsByStage = {};
  for (const result of results) {
    byStage[result.stageId] = result.data;
  }
  return byStage;
}

// --- Initialization fetch functions ---

interface PipelineInitData {
  dealTypes: Record<string, DealType>;
  defaultCategoryId: string;
  configMap: Record<string, DealTypeConfig>;
  stagesByCategory: Record<string, DealStage[]>;
  people: PersonOption[];
}

async function fetchPipelineInit(): Promise<PipelineInitData> {
  // 1. Fetch deal types (categories)
  let nextToken: string | null = null;
  const categories: DealType[] = [];
  let defaultCategoryId = '';

  // GG-3626: listStageCategorys(filter:…) is an auto-generated full-table
  // SCAN — every page of EVERY org's categories, post-filtered (the HAR
  // showed ~70 serial round trips per load, almost all returning 0 items).
  // Any page failing mid-walk on a cold first navigation left categories
  // empty. The byOrgID GSI query returns exactly this org's rows in one
  // call, deterministically.
  const listCategoriesQuery: any = gql`
    query MyQuery($orgID: ID!, $nextToken: String) {
      listStageCategoryByOrgID(orgID: $orgID, nextToken: $nextToken) {
        items {
          id
          title
          enableDeal
          isDefault
        }
        nextToken
      }
    }
  `;

  do {
    const res: any = await API.graphql({
      query: listCategoriesQuery,
      variables: { orgID: global.me.orgID, nextToken },
    });
    categories.push(...res.data.listStageCategoryByOrgID.items);
    nextToken = res.data.listStageCategoryByOrgID.nextToken;
  } while (nextToken);

  for (const cat of categories) {
    if (cat.enableDeal) {
      defaultCategoryId = cat.id;
      if (cat.isDefault) break;
    }
  }

  const filtered = categories.filter((c) => c.enableDeal);
  const dealTypes = filtered.reduce(
    (acc, c) => ({ ...acc, [c.id]: c }),
    {} as Record<string, DealType>
  );

  // 2. Fetch configs for all deal types
  const getCategoryQuery: any = gql`
    query MyQuery($categoryID: ID!) {
      getStageCategory(id: $categoryID) {
        flexAttributes
      }
    }
  `;

  const configMap: Record<string, DealTypeConfig> = {};
  for (const dealType of filtered) {
    const res: any = await API.graphql({
      query: getCategoryQuery,
      variables: { categoryID: dealType.id },
    });
    const flexAttrs = JSON.parse(res.data.getStageCategory.flexAttributes);
    configMap[dealType.id] = flexAttrs.config.deal;
  }

  // 3. Fetch stages for the default category
  const stagesByCategory: Record<string, DealStage[]> = {};
  stagesByCategory[defaultCategoryId] = await fetchStagesForCategory(
    defaultCategoryId
  );

  // 4. Fetch people
  const people = await fetchPeople();

  return { dealTypes, defaultCategoryId, configMap, stagesByCategory, people };
}

async function fetchStagesForCategory(catId: string): Promise<DealStage[]> {
  let nextToken: string | null = null;
  const stages: DealStage[] = [];

  const query: any = gql`
    query MyQuery($categoryID: ID!, $nextToken: String) {
      listStageByCategorySequence(
        categoryID: $categoryID
        filter: { isDeleted: { eq: false } }
        limit: 10000
        nextToken: $nextToken
      ) {
        nextToken
        items {
          id
          sequence
          title
          description
          requireCloser
          userSelectable
        }
      }
    }
  `;

  do {
    const res: any = await API.graphql({
      query,
      variables: { categoryID: catId, nextToken },
    });
    const items = res.data.listStageByCategorySequence.items;
    for (const stage of items) {
      if (stage.sequence >= 1) {
        stages.push({
          id: stage.id,
          name: stage.title,
          value: stage.id,
          label: stage.title,
          display: stage.title,
          categoryID: catId,
          requireCloser: stage.requireCloser,
          sequence: stage.sequence,
          userSelectable: stage.userSelectable,
        });
      }
    }
    nextToken = res.data.listStageByCategorySequence.nextToken;
  } while (nextToken);

  return stages.sort((a, b) => a.sequence - b.sequence);
}

async function fetchPeople(): Promise<PersonOption[]> {
  const query: any = gql`
    query MyQuery($orgID: ID!, $nextToken: String) {
      listUsers(
        filter: { orgID: { eq: $orgID }, status: { ne: "inactive" } }
        limit: 1000
        nextToken: $nextToken
      ) {
        items {
          id
          name
          status
        }
        nextToken
      }
    }
  `;

  let nextToken: string | null = null;
  const users: { id: string; name: string; status?: string }[] = [];
  const people: PersonOption[] = [
    {
      id: 'all',
      name: `All ${global.appSettings?.labels?.person?.plural || 'People'}`,
      label: `All ${global.appSettings?.labels?.person?.plural || 'People'}`,
      value: 'all',
    },
  ];

  do {
    const res: any = await API.graphql({
      query,
      variables: { orgID: global.me.orgID, nextToken },
    });
    users.push(...res.data.listUsers.items);
    nextToken = res.data.listUsers.nextToken;
  } while (nextToken);

  // A–Z, deleted users last and labeled: a re-invited rep otherwise shows up
  // twice with the same name, and the deleted profile can still own deals.
  const isDeleted = (u: { status?: string }) => u.status === 'deleted';
  users.sort(
    (a, b) =>
      Number(isDeleted(a)) - Number(isDeleted(b)) ||
      (a.name || '').localeCompare(b.name || '', undefined, {
        sensitivity: 'base',
      })
  );
  for (const user of users) {
    const name = isDeleted(user) ? `${user.name} (deleted)` : user.name;
    people.push({ id: user.id, name, label: name, value: user.id });
  }

  return people;
}

// =============================================================================
// Hook
// =============================================================================

// Persists the user's selected category across navigations within the same session.
// Resets on full page reload (which is fine - they'd expect to start fresh).
let persistedCategoryId: string | null = null;

export function usePipeline() {
  const queryClient = useQueryClient();

  const [categoryId, setCategoryId] = useState<string>(
    persistedCategoryId || ''
  );
  const [searchString, setSearchString] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>(
    global.me.isAdmin ? 'all' : global.me.id
  );

  // Track sort orders per stage (survives React Query refetches)
  const sortOrdersRef = useRef<Record<string, 'ASC' | 'DESC'>>({});

  // Local reducer for optimistic updates (drag-and-drop, load more)
  const [localOverrides, dispatch] = useReducer(dealsReducer, {});
  const localOverridesRef = useRef(localOverrides);
  localOverridesRef.current = localOverrides;

  // Debounce search input
  useEffect(() => {
    if (!searchString) {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchString);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchString]);

  // --- Cached initialization query (deal types, configs, stages, people) ---
  // GG-3626: on the FIRST in-app navigation this page can mount before the
  // auth bootstrap has populated global.me (in-app nav doesn't pass through
  // the bootstrap gate the way a full reload does). The old query fired
  // anyway, fetched with orgID undefined (or lost a race against throttled
  // AppSync calls), and cached the empty/partial "success" for 5 minutes
  // with no retry path — a blank pipeline until manual reload. Fix: treat
  // a missing org or an incomplete init as a FAILURE so React Query's
  // retry ladder keeps re-asking until the bootstrap settles.
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    refetch: refetchInit,
  } = useQuery({
    queryKey: ['pipeline-init', global.me?.orgID],
    queryFn: async () => {
      if (!global.me?.orgID) {
        throw new Error('pipeline-init: org context not ready yet');
      }
      const init = await fetchPipelineInit();
      // Partial-init guard: a default category with zero stages is the
      // exact broken state QA reproduced (tabs render, board blank).
      // Treat it as transient and let the retry ladder re-fetch.
      if (
        init.defaultCategoryId &&
        !(init.stagesByCategory[init.defaultCategoryId] || []).length
      ) {
        throw new Error(
          'pipeline-init: incomplete (no stages for default category)'
        );
      }
      return init;
    },
    retry: 6,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 5 * 60 * 1000, // 5 minutes - this data rarely changes
    refetchOnWindowFocus: false,
  });

  // Derive state from cached init data
  const dealTypes = initData?.dealTypes || {};
  const dealTypesConfigMap = initData?.configMap || {};
  const dealPeople = initData?.people || [];

  // Set categoryId from init data on first load (only if no persisted selection)
  useEffect(() => {
    if (initData?.defaultCategoryId && !categoryId) {
      setCategoryId(initData.defaultCategoryId);
      persistedCategoryId = initData.defaultCategoryId;
    } else if (
      initData?.defaultCategoryId &&
      categoryId &&
      !initData.dealTypes[categoryId]
    ) {
      //
      //  GG-3626: the remembered category belongs to a DIFFERENT org.
      //  persistedCategoryId is module state and survives logout (only a
      //  full reload clears it), so after switching accounts in-app the
      //  stale id shadowed the new org's default and the stages lookup
      //  missed — "No pipeline stages configured" with the data loaded.
      //  A category unknown to this org's init data is never valid: fall
      //  back to the org's default.
      //
      setCategoryId(initData.defaultCategoryId);
      persistedCategoryId = initData.defaultCategoryId;
    }
  }, [initData, categoryId]);

  // Stages for the current category (cached in initData or fetched separately)
  const [extraStages, setExtraStages] = useState<Record<string, DealStage[]>>(
    {}
  );

  // If we have a persisted category that isn't in initData, fetch its stages
  useEffect(() => {
    if (
      categoryId &&
      initData &&
      !initData.stagesByCategory[categoryId] &&
      !extraStages[categoryId]
    ) {
      fetchStagesForCategory(categoryId).then((stages) => {
        setExtraStages((prev) => ({ ...prev, [categoryId]: stages }));
      });
    }
  }, [categoryId, initData, extraStages]);

  const dealStages = React.useMemo(() => {
    if (!categoryId) return [];
    // Check init data first
    if (initData?.stagesByCategory[categoryId]) {
      return initData.stagesByCategory[categoryId];
    }
    // Check extra stages (fetched on category change)
    if (extraStages[categoryId]) {
      return extraStages[categoryId];
    }
    return [];
  }, [categoryId, initData?.stagesByCategory, extraStages]);

  // --- React Query for deals (cached, parallel, stale-while-revalidate) ---
  const displayFields =
    categoryId && dealTypesConfigMap[categoryId]
      ? dealTypesConfigMap[categoryId].pickerDisplayFields || []
      : [];

  const pipelineQueryKey = [
    'pipeline-deals',
    categoryId,
    selectedOwnerId,
    debouncedSearch,
    dealStages.map((s) => s.id).join(','),
  ];

  const { data: queryData, isFetching: isQueryFetching } = useQuery({
    queryKey: pipelineQueryKey,
    queryFn: () =>
      fetchAllStagesDeals(
        dealStages,
        categoryId,
        displayFields,
        selectedOwnerId,
        debouncedSearch,
        sortOrdersRef.current
      ),
    enabled:
      dealStages.length > 0 && !!categoryId && !!dealTypesConfigMap[categoryId],
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Has cached data? If so, never show the full-page loading spinner
  const hasCachedData = !!queryData;

  // Merge query data with local overrides (optimistic updates take precedence)
  const dealsByStage: DealsByStage = React.useMemo(() => {
    const base = queryData || {};
    const merged: DealsByStage = {};

    for (const stage of dealStages) {
      if (localOverridesRef.current[stage.id]) {
        merged[stage.id] = localOverridesRef.current[stage.id];
      } else if (base[stage.id]) {
        merged[stage.id] = base[stage.id];
      } else {
        merged[stage.id] = {
          deals: [],
          sortOrder: sortOrdersRef.current[stage.id] || 'DESC',
          currentOffset: 0,
          canLoadMore: false,
          isLoading: !hasCachedData,
        };
      }
    }

    return merged;
  }, [queryData, localOverrides, dealStages, hasCachedData]);

  // --- Load More (appends to local overrides) ---
  const loadMore = useCallback(
    async (stageId: string) => {
      if (!categoryId || !dealTypesConfigMap[categoryId]) return;

      const currentData =
        localOverridesRef.current[stageId] || queryData?.[stageId];
      if (!currentData?.canLoadMore || currentData.isLoading) return;

      dispatch({
        type: UPDATE_STAGE,
        payload: { stageId, updates: { isLoading: true } },
      });

      try {
        const { deals: newDeals, canLoadMore } = await fetchDealsForStage(
          categoryId,
          stageId,
          selectedOwnerId === 'all' ? '' : selectedOwnerId,
          debouncedSearch,
          currentData.sortOrder,
          displayFields,
          currentData.currentOffset
        );

        dispatch({
          type: UPDATE_STAGE,
          payload: {
            stageId,
            updates: {
              deals: [...currentData.deals, ...newDeals],
              currentOffset: currentData.currentOffset + newDeals.length,
              canLoadMore,
              isLoading: false,
            },
          },
        });
      } catch (err) {
        console.error('Error loading more deals:', err);
        dispatch({
          type: UPDATE_STAGE,
          payload: { stageId, updates: { isLoading: false } },
        });
      }
    },
    [
      categoryId,
      dealTypesConfigMap,
      selectedOwnerId,
      debouncedSearch,
      displayFields,
      queryData,
    ]
  );

  // --- Move Deal (optimistic with rollback) ---
  const moveDeal = useCallback(
    async (dealId: string, fromStageId: string, toStageId: string) => {
      if (fromStageId === toStageId) return;

      const fromData =
        localOverridesRef.current[fromStageId] || queryData?.[fromStageId];
      const toData =
        localOverridesRef.current[toStageId] || queryData?.[toStageId];
      // Callers treat a resolved promise as "the move happened", so anything
      // that stops us short of the mutation has to throw — a deep-linked or
      // paginated-out deal is not loaded in its column and lands here.
      if (!fromData || !toData) {
        throw new Error('Pipeline data for this stage is not loaded');
      }

      const dealIndex = fromData.deals.findIndex((d) => d.id === dealId);
      if (dealIndex === -1) {
        throw new Error('This deal is not loaded in its current stage');
      }

      const deal = fromData.deals[dealIndex];

      // Optimistic update - local overrides for immediate UI feedback
      dispatch({
        type: UPDATE_STAGE,
        payload: {
          stageId: fromStageId,
          updates: {
            deals: fromData.deals.filter((d) => d.id !== dealId),
            currentOffset: fromData.currentOffset - 1,
          },
        },
      });

      dispatch({
        type: UPDATE_STAGE,
        payload: {
          stageId: toStageId,
          updates: {
            deals: [{ ...deal, stageId: toStageId }, ...toData.deals],
            currentOffset: toData.currentOffset + 1,
          },
        },
      });

      // Also update the React Query cache so the move persists across navigations
      queryClient.setQueryData(
        pipelineQueryKey,
        (old: DealsByStage | undefined) => {
          if (!old) return old;
          const oldFrom = old[fromStageId];
          const oldTo = old[toStageId];
          if (!oldFrom || !oldTo) return old;

          return {
            ...old,
            [fromStageId]: {
              ...oldFrom,
              deals: oldFrom.deals.filter((d) => d.id !== dealId),
              currentOffset: oldFrom.currentOffset - 1,
            },
            [toStageId]: {
              ...oldTo,
              deals: [{ ...deal, stageId: toStageId }, ...oldTo.deals],
              currentOffset: oldTo.currentOffset + 1,
            },
          };
        }
      );

      try {
        // completeMission is the single writer for a stage-only change: it sets
        // currentStageID, derives the disposition from the stage's
        // changeToDispositionID, writes the stage history, and deliberately
        // leaves the deal owner alone. We do NOT call maintainDealOwners here —
        // it rewrites currentOwnerID and appends a deal comment on every drag,
        // neither of which a drag gesture should do.
        // `name` is required even though we are not renaming: completeMission
        // rebuilds searchString/sortName from it unconditionally, so omitting it
        // blanks sortName and drops the name from the deal's search index.
        const res: any = await API.graphql({
          query: mutations.completeMission,
          variables: {
            missionInfo: JSON.stringify({
              dealID: dealId,
              stageID: toStageId,
              name: deal.name,
              userID: global.me.id,
              tmst: new Date().toISOString(),
            }),
          },
        });

        // The lambda reports failure in a successful GraphQL response, so an
        // unchecked await always looks like success
        const status = parseMutationStatus(res?.data?.completeMission);
        if (status !== 'success') {
          throw new Error(`completeMission failed: ${status}`);
        }
      } catch (err) {
        console.error('Error moving deal:', err);

        // Roll this one deal back against CURRENT state. Restoring the whole
        // column snapshots taken before the await would clobber any other move
        // that landed while this one was in flight (duplicating that card).
        const revertStage = (
          stage: DealsByStage[string] | undefined,
          direction: 'restore' | 'remove'
        ) => {
          if (!stage) return stage;
          if (direction === 'remove') {
            if (!stage.deals.some((d) => d.id === dealId)) return stage;
            return {
              ...stage,
              deals: stage.deals.filter((d) => d.id !== dealId),
              currentOffset: stage.currentOffset - 1,
            };
          }
          if (stage.deals.some((d) => d.id === dealId)) return stage;
          return {
            ...stage,
            deals: [deal, ...stage.deals],
            currentOffset: stage.currentOffset + 1,
          };
        };

        const currentFrom =
          localOverridesRef.current[fromStageId] || queryData?.[fromStageId];
        const currentTo =
          localOverridesRef.current[toStageId] || queryData?.[toStageId];

        const revertedFrom = revertStage(currentFrom, 'restore');
        const revertedTo = revertStage(currentTo, 'remove');

        if (revertedFrom) {
          dispatch({
            type: UPDATE_STAGE,
            payload: { stageId: fromStageId, updates: revertedFrom },
          });
        }
        if (revertedTo) {
          dispatch({
            type: UPDATE_STAGE,
            payload: { stageId: toStageId, updates: revertedTo },
          });
        }

        // Revert the React Query cache too, off its own current value
        queryClient.setQueryData(
          pipelineQueryKey,
          (old: DealsByStage | undefined) => {
            if (!old) return old;
            const rolledFrom = revertStage(old[fromStageId], 'restore');
            const rolledTo = revertStage(old[toStageId], 'remove');
            return {
              ...old,
              ...(rolledFrom ? { [fromStageId]: rolledFrom } : {}),
              ...(rolledTo ? { [toStageId]: rolledTo } : {}),
            };
          }
        );

        // completeMission is not atomic — it writes the deal before the work
        // that can fail, so a failure does not prove the move did not land.
        // Refetch so the board converges on server truth.
        queryClient.invalidateQueries({ queryKey: pipelineQueryKey });

        throw err;
      }
    },
    [queryData, queryClient, pipelineQueryKey]
  );

  // --- Sort Order Change (refetches single stage) ---
  const changeSortOrder = useCallback(
    async (stageId: string, order: 'ASC' | 'DESC') => {
      sortOrdersRef.current[stageId] = order;

      dispatch({
        type: UPDATE_STAGE,
        payload: {
          stageId,
          updates: {
            deals: [],
            sortOrder: order,
            currentOffset: 0,
            canLoadMore: false,
            isLoading: true,
          },
        },
      });

      try {
        const { deals, canLoadMore } = await fetchDealsForStage(
          categoryId,
          stageId,
          selectedOwnerId === 'all' ? '' : selectedOwnerId,
          debouncedSearch,
          order,
          displayFields
        );

        dispatch({
          type: UPDATE_STAGE,
          payload: {
            stageId,
            updates: {
              deals,
              sortOrder: order,
              currentOffset: deals.length,
              canLoadMore,
              isLoading: false,
            },
          },
        });
      } catch (err) {
        console.error('Error changing sort order:', err);
        dispatch({
          type: UPDATE_STAGE,
          payload: { stageId, updates: { isLoading: false } },
        });
      }
    },
    [categoryId, selectedOwnerId, debouncedSearch, displayFields]
  );

  // --- Category Change ---
  const changeCategory = useCallback(
    async (newCategoryId: string) => {
      if (newCategoryId === categoryId) return;

      setCategoryId(newCategoryId);
      persistedCategoryId = newCategoryId;

      // If we don't have stages for this category yet, fetch them
      if (
        !initData?.stagesByCategory[newCategoryId] &&
        !extraStages[newCategoryId]
      ) {
        const stages = await fetchStagesForCategory(newCategoryId);
        setExtraStages((prev) => ({ ...prev, [newCategoryId]: stages }));
      }

      // Reset local overrides and sort orders
      sortOrdersRef.current = {};
      dispatch({ type: SET_DEALS, payload: {} });
    },
    [categoryId, initData?.stagesByCategory, extraStages]
  );

  // --- Public fetchDeals (invalidates React Query cache to force refetch) ---
  const fetchDeals = useCallback(
    (stageIds?: string[], sortOrder?: 'ASC' | 'DESC') => {
      if (stageIds) {
        for (const stageId of stageIds) {
          if (sortOrder) {
            sortOrdersRef.current[stageId] = sortOrder;
          }
        }
      }
      dispatch({ type: SET_DEALS, payload: {} });
      queryClient.invalidateQueries({ queryKey: pipelineQueryKey });
    },
    [queryClient, pipelineQueryKey]
  );

  // Clear local overrides when query data refreshes
  useEffect(() => {
    if (queryData) {
      dispatch({ type: SET_DEALS, payload: {} });
    }
  }, [queryData]);

  // Determine loading state:
  // - Show full loading spinner only on first ever load (no cached data at all)
  // - Once we have data, background refetches don't block the UI
  const isLoading = isInitLoading && !initData;

  return {
    // Data
    dealTypes,
    categoryId,
    dealStages,
    dealsByStage,
    dealPeople,

    // Loading states
    isLoading,
    isInitError,
    retryInit: refetchInit,
    isPeopleLoading: isInitLoading && !initData,
    isRefreshing: isQueryFetching && hasCachedData,

    // Filters
    searchString,
    setSearchString,
    selectedOwnerId,
    setSelectedOwnerId,

    // Actions
    fetchDeals,
    loadMore,
    moveDeal,
    changeSortOrder,
    changeCategory,
  };
}
